import { Check, ChevronRight, Users } from 'lucide-react';
import type {
  CompetitionRegistrationEntry,
  PublicCompetition,
  PublicCompetitionDivision,
} from '@tark/shared/lib';
import {
  findDivisionEntry,
  formatCompetitionDate,
  formatDivisionFormat,
  getDivisionAvailabilityLabel,
  getEntryStatusLabel,
} from '../../pages/competitions/publicCompetitionUtils';

export function CompetitionDivisionList({
  competition,
  divisions,
  entries,
  selectedDivisionPublicId,
  onSelect,
}: {
  competition: PublicCompetition;
  divisions: PublicCompetitionDivision[];
  entries?: CompetitionRegistrationEntry[];
  selectedDivisionPublicId?: string;
  onSelect: (division: PublicCompetitionDivision) => void;
}) {
  if (divisions.length === 0) {
    return (
      <section aria-labelledby="competition-divisions-heading">
        <h2 id="competition-divisions-heading" className="text-xl font-bold text-text">Divisions</h2>
        <div className="mt-4 rounded-lg border border-border bg-surface px-4 py-5 text-sm text-text-muted">
          Divisions are not available yet. Check back after the organizer publishes them.
        </div>
      </section>
    );
  }
  return (
    <section aria-labelledby="competition-divisions-heading">
      <div className="mb-4">
        <h2 id="competition-divisions-heading" className="text-xl font-bold text-text">Choose a division</h2>
        <p className="mt-1 text-sm text-text-muted">Select one division to review registration.</p>
      </div>

      <div className="space-y-3">
        {divisions.map((division) => {
          const selected = selectedDivisionPublicId === division.divisionPublicId;
          const entry = findDivisionEntry(entries, division.divisionPublicId);
          const deadline = formatCompetitionDate(
            division.matchDeadlineAt,
            competition.timezone
          );
          return (
            <button
              key={division.divisionPublicId}
              type="button"
              aria-pressed={selected}
              onClick={() => onSelect(division)}
              className={`flex w-full items-center gap-4 rounded-lg border px-4 py-4 text-left transition-colors ${
                selected
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-surface hover:border-primary/35 hover:bg-surface-elevated'
              }`}
            >
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                  selected ? 'bg-primary text-white' : 'bg-surface-elevated text-primary'
                }`}
              >
                {selected ? <Check className="h-4 w-4" /> : <Users className="h-4 w-4" />}
              </span>

              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-semibold text-text">{division.name}</span>
                  {entry ? (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {getEntryStatusLabel(entry)}
                    </span>
                  ) : null}
                </span>
                <span className="mt-1 block text-sm text-text-muted">
                  {[formatDivisionFormat(division.formatCode), division.levelLabel]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                <span className="mt-1 block text-xs text-text-subtle">
                  {[
                    division.capacity ? `Capacity ${division.capacity}` : null,
                    deadline ? `Matches due ${deadline}` : null,
                    getDivisionAvailabilityLabel(competition, division),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>

              <ChevronRight className="h-4 w-4 shrink-0 text-text-subtle" />
            </button>
          );
        })}
      </div>
    </section>
  );
}
